/* eslint-disable */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { currencyLabel } from "@/lib/currency";
import { toast } from "sonner";
import {
  ArrowLeft, LayoutDashboard, Package, HardHat, Boxes, ListChecks,
  TriangleAlert, Sparkles, FileText, Plus, Trash2, Loader2, Check,
  CircleDot, Circle, Activity, FileSpreadsheet,
} from "lucide-react";

const money = (v) => (v ?? 0).toLocaleString("ro-RO", { maximumFractionDigits: 0 });

function Metric({ label, value, unit, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-mono-plex text-2xl lg:text-3xl font-semibold mt-2 ${accent || "text-[#E2E8F0]"}`}>
        {value}
      </div>
      <div className="text-xs text-[#94A3B8] mt-0.5">{unit}</div>
    </div>
  );
}

function Bar({ label, value, total, color = "#00BFFF", cur }) {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-[#94A3B8]">{label}</span>
        <span className="font-mono-plex text-[#E2E8F0]">{money(value)} {cur}</span>
      </div>
      <div className="h-2.5 rounded-full bg-[#1E293B] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function ProjectManager() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const cur = currencyLabel(i18n.language);
  const TABS = [
    { k: "overview", l: t("projectManager.tab_overview"), icon: LayoutDashboard },
    { k: "materiale", l: t("projectManager.tab_materiale"), icon: Package },
    { k: "manopera", l: t("projectManager.tab_manopera"), icon: HardHat },
    { k: "consumabile", l: t("projectManager.tab_consumabile"), icon: Boxes },
    { k: "progres", l: t("projectManager.tab_progres"), icon: ListChecks },
    { k: "alerte", l: t("projectManager.tab_alerte"), icon: TriangleAlert },
    { k: "recomandari", l: t("projectManager.tab_recomandari"), icon: Sparkles },
    { k: "sumar", l: t("projectManager.tab_sumar"), icon: FileText },
  ];
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState("");
  const [reportBusy, setReportBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${id}/project`);
      setJob(r.data);
    } catch (e) {
      toast.error(t("projectManager.jobNotFound"));
      nav("/app/lucrari");
    }
  };
  useEffect(() => { load(); }, [id]);

  const downloadReport = async (fmt) => {
    setReportBusy(fmt);
    try {
      const res = await api.get(`/jobs/${id}/project-report?format=${fmt}&with_ai=true&lang=${i18n.language}`, { responseType: "blob" });
      const map = { pdf: "application/pdf", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" };
      const url = window.URL.createObjectURL(new Blob([res.data], { type: map[fmt] }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(job?.title || "proiect").replace(/[^a-z0-9]+/gi, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      load();
    } catch (e) {
      toast.error(t("projectManager.reportErr"));
    } finally { setReportBusy(""); }
  };

  if (!job) return <div className="p-10 text-sm text-[#94A3B8]">{t("projectManager.loading")}</div>;
  const p = job.project || {};

  return (
    <div className="p-5 lg:p-10 max-w-6xl mx-auto" data-testid="project-manager">
      <Link to={`/app/lucrari/${id}`} className="text-sm text-[#94A3B8] hover:text-[#00BFFF] flex items-center gap-1 transition-colors">
        <ArrowLeft size={14} /> {t("projectManager.back")}
      </Link>

      {/* Header */}
      <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#00BFFF]">AI Project Manager</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">{job.title}</h1>
          <p className="text-sm text-[#94A3B8] mt-1">{job.client_name || "—"}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2">
            <button onClick={() => downloadReport("pdf")} disabled={!!reportBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="export-pdf-btn">
              {reportBusy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} PDF
            </button>
            <button onClick={() => downloadReport("xlsx")} disabled={!!reportBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50" data-testid="export-xlsx-btn">
              {reportBusy === "xlsx" ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Excel
            </button>
          </div>
          <div className="card-flat px-5 py-3 flex items-center gap-3 glow-blue">
            <Activity size={18} className="text-[#00BFFF]" />
            <div>
              <div className="overline">{t("projectManager.progress")}</div>
              <div className="font-mono-plex text-xl font-semibold text-[#00BFFF]">{p.progress_percent ?? 0}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="pm-tabs">
        {TABS.map((tb) => (
          <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`tab-${tb.k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold whitespace-nowrap rounded-lg border transition-colors ${
              tab === tb.k ? "bg-[#00BFFF] text-[#03070E] border-[#00BFFF]" : "bg-[#0B111D] text-[#94A3B8] border-[#1E293B] hover:text-[#E2E8F0] hover:border-[#2B3B55]"
            }`}>
            <tb.icon size={15} /> {tb.l}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "overview" && <Overview job={job} p={p} cur={cur} />}
        {tab === "materiale" && <Materials job={job} setJob={setJob} id={id} busy={busy} setBusy={setBusy} />}
        {tab === "manopera" && <Labor job={job} setJob={setJob} id={id} busy={busy} setBusy={setBusy} />}
        {tab === "consumabile" && <Consumables job={job} setJob={setJob} id={id} busy={busy} setBusy={setBusy} />}
        {tab === "progres" && <Progress job={job} setJob={setJob} id={id} busy={busy} setBusy={setBusy} />}
        {tab === "alerte" && <Alerts job={job} setJob={setJob} id={id} />}
        {tab === "recomandari" && <Recommendations job={job} setJob={setJob} id={id} />}
        {tab === "sumar" && <Summary job={job} setJob={setJob} id={id} />}
      </div>
    </div>
  );
}

/* ---------- Overview ---------- */
function Overview({ job, p, cur }) {
  const { t } = useTranslation();
  const profitPos = (p.profit ?? 0) >= 0;
  return (
    <div className="space-y-6" data-testid="pm-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label={t("projectManager.budget")} value={money(p.budget)} unit={cur} testid="metric-budget" />
        <Metric label={t("projectManager.spent")} value={money(p.spent)} unit={cur} accent="text-[#FBBF24]" testid="metric-spent" />
        <Metric label={t("projectManager.projected")} value={money(p.projected)} unit={cur} accent="text-[#94A3B8]" testid="metric-projected" />
        <div className="card-flat p-5" data-testid="metric-margin">
          <div className="overline">{t("projectManager.profitMargin")}</div>
          <div className={`font-mono-plex text-2xl lg:text-3xl font-semibold mt-2 ${profitPos ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
            {money(p.profit)}
          </div>
          <div className={`text-xs mt-0.5 ${profitPos ? "text-[#22C55E]" : "text-[#EF4444]"}`}>{t("projectManager.marginSuffix", { pct: p.margin_percent ?? 0 })}</div>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="overline mb-4">{t("projectManager.jobProgress")}</div>
        <div className="h-4 rounded-full bg-[#1E293B] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#0091CC] to-[#00BFFF] transition-all duration-700"
            style={{ width: `${p.progress_percent ?? 0}%` }} />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-[#94A3B8]">
          <span>{t("projectManager.tasksDone", { done: p.task_counts?.done ?? 0, total: p.task_counts?.total ?? 0 })}</span>
          <span className="font-mono-plex text-[#00BFFF] text-sm">{p.progress_percent ?? 0}%</span>
        </div>
      </div>

      <div className="card-flat p-6">
        <div className="overline mb-4">{t("projectManager.expenseStructure")}</div>
        <div className="space-y-4">
          <Bar label={t("projectManager.materials")} value={p.materials_total} total={p.spent || 1} color="#00BFFF" cur={cur} />
          <Bar label={t("projectManager.labor")} value={p.labor_total} total={p.spent || 1} color="#38BDF8" cur={cur} />
          <Bar label={t("projectManager.consumables")} value={p.consumables_total} total={p.spent || 1} color="#818CF8" cur={cur} />
          <Bar label={t("projectManager.otherExpenses")} value={p.expenses_total} total={p.spent || 1} color="#94A3B8" cur={cur} />
        </div>
      </div>
    </div>
  );
}

/* ---------- Generic items table ---------- */
function ItemSection({ columns, rows, onDelete, form, testid }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-testid={testid}>
      {form}
      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#111C30] text-left">
            {columns.map((c) => <th key={c.k} className={`p-3 text-[#94A3B8] font-semibold ${c.right ? "text-right" : ""}`}>{c.l}</th>)}
            <th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="p-6 text-center text-[#94A3B8]">{t("projectManager.itemsEmpty")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-[#1E293B] hover:bg-[#111C30]/50 transition-colors" data-testid={`row-${r.id}`}>
                {columns.map((c) => <td key={c.k} className={`p-3 ${c.right ? "text-right font-mono-plex" : ""} ${c.bold ? "font-semibold text-[#E2E8F0]" : "text-[#CBD5E1]"}`}>{c.render ? c.render(r) : r[c.k]}</td>)}
                <td className="p-3 text-right">
                  <button onClick={() => onDelete(r.id)} className="text-[#94A3B8] hover:text-[#EF4444] transition-colors" data-testid={`del-${r.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{label}</label>
      <input className="input-flat mt-1" {...props} />
    </div>
  );
}

/* ---------- Materials ---------- */
function Materials({ job, setJob, id, busy, setBusy }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", quantity: "1", unit: "buc", unit_price: "" });
  const add = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error(t("projectManager.addName"));
    setBusy("mat");
    try {
      const r = await api.post(`/jobs/${id}/materials`, { name: f.name, quantity: parseFloat(f.quantity) || 0, unit: f.unit, unit_price: parseFloat(f.unit_price) || 0 });
      setJob(r.data); setF({ name: "", quantity: "1", unit: "buc", unit_price: "" });
    } catch { toast.error(t("projectManager.addErr")); } finally { setBusy(""); }
  };
  const del = async (mid) => { const r = await api.delete(`/jobs/${id}/materials/${mid}`); setJob(r.data); };
  const form = (
    <form onSubmit={add} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="material-form">
      <div className="sm:col-span-4"><Field label={t("projectManager.matName")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t("projectManager.matNamePh")} data-testid="mat-name" /></div>
      <div className="sm:col-span-2"><Field label={t("projectManager.qty")} type="number" step="0.01" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="mat-qty" /></div>
      <div className="sm:col-span-2"><Field label={t("projectManager.unit")} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="mat-unit" /></div>
      <div className="sm:col-span-3"><Field label={t("projectManager.unitPrice")} type="number" step="0.01" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="mat-price" /></div>
      <div className="sm:col-span-1"><button disabled={busy === "mat"} className="btn-primary w-full flex items-center justify-center" data-testid="add-material-btn">{busy === "mat" ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}</button></div>
    </form>
  );
  return <ItemSection testid="pm-materiale" form={form} rows={job.materials || []} onDelete={del}
    columns={[
      { k: "name", l: t("projectManager.matName"), bold: true },
      { k: "quantity", l: t("projectManager.qty"), right: true, render: (r) => `${(r.quantity ?? 0)} ${r.unit || ""}` },
      { k: "unit_price", l: t("projectManager.unitPrice"), right: true, render: (r) => (r.unit_price ?? 0).toFixed(2) },
      { k: "amount", l: t("projectManager.sum"), right: true, bold: true, render: (r) => (r.amount ?? 0).toFixed(2) },
    ]} />;
}

/* ---------- Labor ---------- */
function Labor({ job, setJob, id, busy, setBusy }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ worker: "", role: "", hours: "", rate: "", unit: "ore" });
  const add = async (e) => {
    e.preventDefault();
    if (!f.worker.trim()) return toast.error(t("projectManager.addWorkerErr"));
    setBusy("lab");
    try {
      const r = await api.post(`/jobs/${id}/labor`, { worker: f.worker, role: f.role, hours: parseFloat(f.hours) || 0, rate: parseFloat(f.rate) || 0, unit: f.unit });
      setJob(r.data); setF({ worker: "", role: "", hours: "", rate: "", unit: "ore" });
    } catch { toast.error(t("projectManager.addErr")); } finally { setBusy(""); }
  };
  const del = async (lid) => { const r = await api.delete(`/jobs/${id}/labor/${lid}`); setJob(r.data); };
  const form = (
    <form onSubmit={add} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="labor-form">
      <div className="sm:col-span-3"><Field label={t("projectManager.worker")} value={f.worker} onChange={(e) => setF({ ...f, worker: e.target.value })} placeholder={t("projectManager.workerPh")} data-testid="lab-worker" /></div>
      <div className="sm:col-span-3"><Field label={t("projectManager.role")} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder={t("projectManager.rolePh")} data-testid="lab-role" /></div>
      <div className="sm:col-span-2"><Field label={t("projectManager.hoursDays")} type="number" step="0.01" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="lab-hours" /></div>
      <div className="sm:col-span-3"><Field label={t("projectManager.rate")} type="number" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="lab-rate" /></div>
      <div className="sm:col-span-1"><button disabled={busy === "lab"} className="btn-primary w-full flex items-center justify-center" data-testid="add-labor-btn">{busy === "lab" ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}</button></div>
    </form>
  );
  return <ItemSection testid="pm-manopera" form={form} rows={job.labor || []} onDelete={del}
    columns={[
      { k: "worker", l: t("projectManager.worker"), bold: true },
      { k: "role", l: t("projectManager.role") },
      { k: "hours", l: t("projectManager.hoursCol"), right: true, render: (r) => `${(r.hours ?? 0)} ${r.unit || ""}` },
      { k: "rate", l: t("projectManager.rateCol"), right: true, render: (r) => (r.rate ?? 0).toFixed(2) },
      { k: "amount", l: t("projectManager.sum"), right: true, bold: true, render: (r) => (r.amount ?? 0).toFixed(2) },
    ]} />;
}

/* ---------- Consumables ---------- */
function Consumables({ job, setJob, id, busy, setBusy }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ name: "", quantity: "1", unit: "buc", unit_price: "" });
  const add = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error(t("projectManager.addName"));
    setBusy("con");
    try {
      const r = await api.post(`/jobs/${id}/consumables`, { name: f.name, quantity: parseFloat(f.quantity) || 0, unit: f.unit, unit_price: parseFloat(f.unit_price) || 0 });
      setJob(r.data); setF({ name: "", quantity: "1", unit: "buc", unit_price: "" });
    } catch { toast.error(t("projectManager.addErr")); } finally { setBusy(""); }
  };
  const del = async (cid) => { const r = await api.delete(`/jobs/${id}/consumables/${cid}`); setJob(r.data); };
  const form = (
    <form onSubmit={add} className="card-flat p-5 grid grid-cols-1 sm:grid-cols-12 gap-3 items-end" data-testid="consumable-form">
      <div className="sm:col-span-4"><Field label={t("projectManager.conName")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t("projectManager.conNamePh")} data-testid="con-name" /></div>
      <div className="sm:col-span-2"><Field label={t("projectManager.qty")} type="number" step="0.01" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="con-qty" /></div>
      <div className="sm:col-span-2"><Field label={t("projectManager.unit")} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="con-unit" /></div>
      <div className="sm:col-span-3"><Field label={t("projectManager.unitPrice")} type="number" step="0.01" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="con-price" /></div>
      <div className="sm:col-span-1"><button disabled={busy === "con"} className="btn-primary w-full flex items-center justify-center" data-testid="add-consumable-btn">{busy === "con" ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}</button></div>
    </form>
  );
  return <ItemSection testid="pm-consumabile" form={form} rows={job.consumables || []} onDelete={del}
    columns={[
      { k: "name", l: t("projectManager.conName"), bold: true },
      { k: "quantity", l: t("projectManager.qty"), right: true, render: (r) => `${(r.quantity ?? 0)} ${r.unit || ""}` },
      { k: "unit_price", l: t("projectManager.unitPrice"), right: true, render: (r) => (r.unit_price ?? 0).toFixed(2) },
      { k: "amount", l: t("projectManager.sum"), right: true, bold: true, render: (r) => (r.amount ?? 0).toFixed(2) },
    ]} />;
}

/* ---------- Progress / Tasks ---------- */
function Progress({ job, setJob, id, busy, setBusy }) {
  const { t } = useTranslation();
  const STATUS = {
    todo: { l: t("projectManager.st_todo"), icon: Circle, c: "text-[#94A3B8]" },
    in_progress: { l: t("projectManager.st_in_progress"), icon: CircleDot, c: "text-[#FBBF24]" },
    done: { l: t("projectManager.st_done"), icon: Check, c: "text-[#22C55E]" },
  };
  const [title, setTitle] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error(t("projectManager.addTaskErr"));
    setBusy("task");
    try { const r = await api.post(`/jobs/${id}/tasks`, { title }); setJob(r.data); setTitle(""); }
    catch { toast.error(t("projectManager.error")); } finally { setBusy(""); }
  };
  const cycle = async (tk) => {
    const next = tk.status === "todo" ? "in_progress" : tk.status === "in_progress" ? "done" : "todo";
    const r = await api.put(`/jobs/${id}/tasks/${tk.id}`, { status: next }); setJob(r.data);
  };
  const del = async (tid) => { const r = await api.delete(`/jobs/${id}/tasks/${tid}`); setJob(r.data); };
  const tasks = job.tasks || [];
  const p = job.project || {};
  return (
    <div className="space-y-4" data-testid="pm-progres">
      <div className="card-flat p-6">
        <div className="overline mb-3">{t("projectManager.totalProgress")}</div>
        <div className="h-3 rounded-full bg-[#1E293B] overflow-hidden">
          <div className="h-full rounded-full bg-[#00BFFF] transition-all duration-500" style={{ width: `${p.progress_percent ?? 0}%` }} />
        </div>
        <div className="text-xs text-[#94A3B8] mt-2">{t("projectManager.tasksLine", { done: p.task_counts?.done ?? 0, total: p.task_counts?.total ?? 0, inp: p.task_counts?.in_progress ?? 0 })}</div>
      </div>
      <form onSubmit={add} className="card-flat p-5 flex gap-3 items-end" data-testid="task-form">
        <div className="flex-1"><Field label={t("projectManager.newTask")} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("projectManager.taskPh")} data-testid="task-title" /></div>
        <button disabled={busy === "task"} className="btn-primary flex items-center gap-2" data-testid="add-task-btn">{busy === "task" ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />} {t("projectManager.add")}</button>
      </form>
      <div className="space-y-2">
        {tasks.length === 0 ? (
          <div className="card-flat p-6 text-center text-sm text-[#94A3B8]">{t("projectManager.tasksEmpty")}</div>
        ) : tasks.map((tk) => {
          const s = STATUS[tk.status] || STATUS.todo; const Icon = s.icon;
          return (
            <div key={tk.id} className="card-flat p-4 flex items-center justify-between gap-3" data-testid={`task-${tk.id}`}>
              <button onClick={() => cycle(tk)} className="flex items-center gap-3 flex-1 text-left" data-testid={`cycle-${tk.id}`}>
                <Icon size={20} className={s.c} />
                <span className={`text-sm ${tk.status === "done" ? "line-through text-[#94A3B8]" : "text-[#E2E8F0]"}`}>{tk.title}</span>
              </button>
              <span className={`text-xs font-semibold ${s.c}`}>{s.l}</span>
              <button onClick={() => del(tk.id)} className="text-[#94A3B8] hover:text-[#EF4444] transition-colors" data-testid={`del-task-${tk.id}`}><Trash2 size={16} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- AI Alerts ---------- */
function Alerts({ job, setJob, id }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post(`/jobs/${id}/ai-alerts?lang=${i18n.language}`); setJob({ ...job, alerts: r.data.alerts }); if (!r.data.alerts.length) toast.success(t("projectManager.noRisk")); }
    catch { toast.error(t("projectManager.alertsErr")); } finally { setBusy(false); }
  };
  const alerts = job.alerts || [];
  const styles = { danger: "border-[#EF4444]/60 bg-[#EF4444]/10 text-[#EF4444]", warning: "border-[#FBBF24]/60 bg-[#FBBF24]/10 text-[#FBBF24]", info: "border-[#00BFFF]/60 bg-[#00BFFF]/10 text-[#00BFFF]" };
  return (
    <div className="space-y-4" data-testid="pm-alerte">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#94A3B8] max-w-lg">{t("projectManager.alertsDesc")}</p>
        <button onClick={gen} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="gen-alerts-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <TriangleAlert size={16} />} {alerts.length ? t("projectManager.reanalyze") : t("projectManager.analyzeRisks")}
        </button>
      </div>
      {alerts.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-[#94A3B8]" data-testid="alerts-empty">{t("projectManager.alertsEmpty")}</div>
      ) : alerts.map((a) => {
        const st = styles[a.level] || styles.info;
        return (
          <div key={a.id} className={`border rounded-lg p-4 ${st}`} data-testid={`alert-${a.id}`}>
            <div className="flex items-center gap-2 font-semibold"><TriangleAlert size={16} /> {a.title}</div>
            <p className="text-sm text-[#CBD5E1] mt-1">{a.message}</p>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- AI Recommendations ---------- */
function Recommendations({ job, setJob, id }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post(`/jobs/${id}/ai-recommendations?lang=${i18n.language}`); setJob({ ...job, recommendations: r.data.recommendations }); }
    catch { toast.error(t("projectManager.recsErr")); } finally { setBusy(false); }
  };
  const recs = job.recommendations || [];
  return (
    <div className="space-y-4" data-testid="pm-recomandari">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#94A3B8] max-w-lg">{t("projectManager.recsDesc")}</p>
        <button onClick={gen} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="gen-recs-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {recs.length ? t("projectManager.regenerate") : t("projectManager.genRecs")}
        </button>
      </div>
      {recs.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-[#94A3B8]" data-testid="recs-empty">{t("projectManager.recsEmpty")}</div>
      ) : recs.map((r) => (
        <div key={r.id} className="rounded-lg p-5 border border-[#00BFFF]/30 bg-[#00BFFF]/[0.07]" data-testid={`rec-${r.id}`}>
          <div className="flex items-center gap-2 font-semibold text-[#00BFFF]"><Sparkles size={16} /> {r.title}</div>
          <p className="text-sm text-[#CBD5E1] mt-1.5 leading-relaxed">{r.text}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------- AI Summary ---------- */
function Summary({ job, setJob, id }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post(`/jobs/${id}/ai-summary?lang=${i18n.language}`); setJob({ ...job, ai_summary: r.data.summary }); toast.success(t("projectManager.summaryGenerated")); }
    catch { toast.error(t("projectManager.summaryErr")); } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-6" data-testid="pm-sumar">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline">{t("projectManager.summaryTitle")}</div>
        <button onClick={gen} disabled={busy} className="btn-ink flex items-center gap-2 disabled:opacity-50" data-testid="gen-summary-btn">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {job.ai_summary ? t("projectManager.regenerate") : t("projectManager.genSummary")}
        </button>
      </div>
      {job.ai_summary ? (
        <p className="mt-4 text-sm leading-relaxed text-[#CBD5E1]" data-testid="summary-text">{job.ai_summary}</p>
      ) : (
        <p className="mt-4 text-sm text-[#94A3B8]">{t("projectManager.summaryPlaceholder")}</p>
      )}
    </div>
  );
}
